import * as R from "ramda";
import React from "react";
import { FaRegSave } from "react-icons/fa";
import styled from "styled-components";

import colors from "../../../../../styles/colors";
import ClickableElement from "../../../../utils/ClickableElement";
import CheckBox from "../../Checkbox";
import { Input } from "../../FormElements";

const PageLock = styled.div`
  width: 60%;
  margin: 0.5rem 0;
  padding: 2rem 3rem;
  background-color: ${colors.lightgrey2};
  box-shadow: 0.5rem 0.5rem 4rem -1.5rem rgba(0, 0, 0, 0.35);
`;

const ItemLabel = styled.label`
  font-size: inherit;
  font-weight: inherit;
  margin-right: 3rem;
`;

const Label = styled.span`
  margin-right: 1rem;
`;

const TimeUnitInput = styled(Input)`
  width: 6rem;
  height: 1.3rem;
`;

const Form = styled.form``;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const SubmitButton = styled(ClickableElement)`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-right: 1rem;
  padding-right: 1rem;
`;

const EnableLockText = styled.span`
  margin-left: 1rem;
`;

export interface IPageLockSettings {
  isEnabled: boolean;
  minutes: number;
  seconds: number;
}

interface IPageLockSettingsProps {
  modifyPageLockSettings: (data: IPageLockSettings) => void;
  settings: IPageLockSettings | null;
}

enum InputNames {
  EnablePageLock = "enable-page-lock",
  DelayMinutes = "delay-minutes",
  DelaySeconds = "delay-seconds",
}

interface IFormValues {
  [InputNames.EnablePageLock]: boolean;
  [InputNames.DelayMinutes]: number;
  [InputNames.DelaySeconds]: number;
}

const initPageLockSettings: IPageLockSettings = {
  isEnabled: false,
  minutes: 0,
  seconds: 0,
};

const getInitFormData = (settings: IPageLockSettings) => ({
  [InputNames.EnablePageLock]: settings.isEnabled,
  [InputNames.DelayMinutes]: settings.minutes,
  [InputNames.DelaySeconds]: settings.seconds,
});

const haveSettingsChanged = (settings: IPageLockSettings, formData: IFormValues) => {
  return ![
    settings.isEnabled === formData[InputNames.EnablePageLock],
    settings.minutes === formData[InputNames.DelayMinutes],
    settings.seconds === formData[InputNames.DelaySeconds],
  ].every(p => p);
};

export const PageLockSettings: React.FC<IPageLockSettingsProps> = props => {
  const [formData, setFormData] = React.useState<IFormValues>(getInitFormData(props.settings || initPageLockSettings));
  const refObjs = {
    [InputNames.EnablePageLock]: React.createRef<HTMLInputElement>(),
    [InputNames.DelayMinutes]: React.createRef<HTMLInputElement>(),
    [InputNames.DelaySeconds]: React.createRef<HTMLInputElement>(),
  };
  const submitContent = (elem: React.FormEvent) => {
    elem.preventDefault();
    const getChecked = (name: InputNames) => {
      const selector = refObjs[name].current;
      if (!R.isNil(selector)) {
        return selector.checked;
      }
      return false;
    };

    const getValueAsNumber = (name: InputNames) => {
      const selector = refObjs[name].current;
      if (!R.isNil(selector)) {
        return Number(selector.value) || 0;
      }
      return 0;
    };

    const newSettings = {
      isEnabled: getChecked(InputNames.EnablePageLock),
      minutes: getValueAsNumber(InputNames.DelayMinutes),
      seconds: getValueAsNumber(InputNames.DelaySeconds),
    };
    if (haveSettingsChanged(newSettings, formData)) {
      props.modifyPageLockSettings(newSettings);
      setFormData(getInitFormData(newSettings));
    }
  };

  return (
    <PageLock>
      <div>
        <span>Page Lock Settings</span>
      </div>
      <hr />
      <div>
        <h4>Delay</h4>
        <p>Enter the amount of time the page will be locked:</p>
      </div>
      <Form onSubmit={submitContent}>
        <Row>
          <ItemLabel htmlFor={InputNames.DelayMinutes}>
            <Label>Minutes:</Label>
            <TimeUnitInput
              type="number"
              min={0}
              placeholder="Minutes"
              name={InputNames.DelayMinutes}
              ref={refObjs[InputNames.DelayMinutes]}
              defaultValue={`${formData[InputNames.DelayMinutes]}`}
            />
          </ItemLabel>
          <ItemLabel htmlFor={InputNames.DelaySeconds}>
            <Label>Seconds:</Label>
            <TimeUnitInput
              type="number"
              min={0}
              placeholder="Seconds"
              name={InputNames.DelaySeconds}
              ref={refObjs[InputNames.DelaySeconds]}
              defaultValue={`${formData[InputNames.DelaySeconds]}`}
            />
          </ItemLabel>
          <ItemLabel htmlFor={InputNames.EnablePageLock}>
            <CheckBox
              name={InputNames.EnablePageLock}
              defaultChecked={formData[InputNames.EnablePageLock]}
              ref={refObjs[InputNames.EnablePageLock]}
            />
            <EnableLockText>Enable Page Lock</EnableLockText>
          </ItemLabel>
        </Row>
        <SubmitButton>
          <FaRegSave />
          Save
        </SubmitButton>
      </Form>
    </PageLock>
  );
};
